import { pickClass, setActiveClass } from "@/lib/activeClass";
import { useEffect, useMemo, useState } from "react";
import { api, formatError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, ExternalLink } from "lucide-react";

const TRIMESTRES = ["T1", "T2", "T3"];

export default function Notes() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [trimestre, setTrimestre] = useState("T1");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState({}); // key = studentId|subjectId|subSubject
  const [averages, setAverages] = useState({});
  const [dirty, setDirty] = useState({}); // unsaved keys
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      setClasses(data);
      if (data.length > 0) setClassId(pickClass(data));
    });
  }, []);

  const load = async () => {
    if (!classId) return;
    try {
      const [st, sub, gr, av] = await Promise.all([
        api.get("/students", { params: { class_id: classId } }),
        api.get("/subjects", { params: { class_id: classId } }),
        api.get("/grades", { params: { class_id: classId, trimestre } }),
        api.get("/averages", { params: { class_id: classId, trimestre } }),
      ]);
      setStudents(st.data.sort((a, b) => `${a.nom}${a.prenom}`.localeCompare(`${b.nom}${b.prenom}`)));
      setSubjects(sub.data);
      const map = {};
      gr.data.forEach((g) => {
        map[`${g.student_id}|${g.subject_id}|${g.sub_subject || ""}`] = g.valeur;
      });
      setGrades(map);
      setAverages(av.data);
      setDirty({});
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, [classId, trimestre]);

  const cellKey = (sid, subjId, ss) => `${sid}|${subjId}|${ss || ""}`;
  const setValue = (key, v) => {
    setGrades({ ...grades, [key]: v });
    setDirty({ ...dirty, [key]: true });
  };

  const saveAll = async () => {
    setSaving(true);
    let count = 0; let fails = 0;
    for (const k of Object.keys(dirty)) {
      const [student_id, subject_id, sub_subject] = k.split("|");
      const raw = grades[k];
      if (raw === "" || raw === null || raw === undefined) continue;
      const valeur = parseFloat(String(raw).replace(",", "."));
      if (isNaN(valeur) || valeur < 0 || valeur > 10) { fails++; continue; }
      try {
        await api.put("/grades", {
          student_id, subject_id, sub_subject: sub_subject || null, trimestre, valeur,
        });
        count++;
      } catch { fails++; }
    }
    setSaving(false);
    if (count) toast.success(`${count} note(s) enregistrée(s)`);
    if (fails) toast.error(`${fails} échec(s) (valeurs invalides ?)`);
    load();
  };

  const openBulletinsView = (mode = 1) => {
    const url = `${API_BASE}/bulletins/pdf?class_id=${classId}&trimestre=${trimestre}&mode=${mode}`;
    window.open(url, "_blank");
  };

  const headerCells = useMemo(() => {
    // Build (subject, sub) columns
    const cells = [];
    subjects.forEach((s) => {
      if (s.mode === "sous_matieres" && (s.sous_matieres || []).length > 0) {
        s.sous_matieres.forEach((ss) =>
          cells.push({ subjectId: s.id, sub: ss.nom, label: `${s.nom} · ${ss.nom}`, isSub: true })
        );
      } else {
        cells.push({ subjectId: s.id, sub: null, label: s.nom, isSub: false });
      }
    });
    return cells;
  }, [subjects]);

  const canEdit = user.role === "maitre";
  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Saisie des notes</h1>
          <p className="text-sm text-[#5C736A]">Notes sur 10 — la dernière valeur écrase la précédente</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={classId} onValueChange={(v) => { setClassId(v); setActiveClass(v); }}>
            <SelectTrigger className="w-44" data-testid="notes-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Tabs value={trimestre} onValueChange={setTrimestre}>
            <TabsList>
              {TRIMESTRES.map((t) => <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}>{t}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          {canEdit && (
            <Button
              onClick={saveAll}
              disabled={dirtyCount === 0 || saving}
              className="bg-[#1F7A63] hover:bg-[#165c4b] text-white"
              data-testid="save-grades-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? "Enregistrement…" : dirtyCount > 0 ? `Enregistrer ${dirtyCount}` : "Enregistrer"}
            </Button>
          )}
        </div>
      </div>

      <div className="ek-card p-0 overflow-hidden">
        <div className="ek-table-wrap max-h-[70vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#E8F2EF] text-[#1F7A63] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-[#E8F2EF] z-10 min-w-[200px]">Élève</th>
                {headerCells.map((c, i) => (
                  <th key={i} className="text-center px-2 py-2 whitespace-nowrap font-semibold text-xs">
                    {c.label}
                  </th>
                ))}
                <th className="text-center px-3 py-2 font-semibold">Moy. /10</th>
                <th className="text-center px-3 py-2 font-semibold">Rang</th>
              </tr>
            </thead>
            <tbody>
              {students.map((st, idx) => {
                const av = averages[st.id] || {};
                return (
                  <tr key={st.id} className={idx % 2 ? "bg-slate-50" : "bg-white"} data-testid={`note-row-${st.matricule}`}>
                    <td className="px-3 py-2 sticky left-0 bg-inherit z-[5] font-medium">
                      {st.prenom} <span className="font-semibold">{st.nom}</span>
                    </td>
                    {headerCells.map((c, i) => {
                      const key = cellKey(st.id, c.subjectId, c.sub);
                      const val = grades[key] ?? "";
                      return (
                        <td key={i} className="px-1 py-1 text-center">
                          <Input
                            data-testid={`note-${st.matricule}-${c.label.replace(/\s/g, '_')}`}
                            disabled={!canEdit}
                            value={val}
                            onChange={(e) => setValue(key, e.target.value)}
                            className={`h-9 text-center text-sm ${dirty[key] ? "border-[#E6B800] bg-yellow-50" : ""}`}
                            inputMode="decimal"
                            placeholder="—"
                          />
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center font-bold text-[#1F7A63]">
                      {av.moyenne_generale != null ? av.moyenne_generale.toFixed(2) : "—"}
                    </td>
                    <td className="px-3 py-2 text-center">{av.rang ?? "—"}</td>
                  </tr>
                );
              })}
              {students.length === 0 && (
                <tr><td colSpan={headerCells.length + 3} className="text-center py-10 text-[#5C736A] text-sm">Aucun élève dans cette classe.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ek-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-[#5C736A]">
          Astuce : la touche <kbd>Tab</kbd> facilite la saisie ligne par ligne. Cliquez « Enregistrer » pour valider.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openBulletinsView(3)} data-testid="see-class-bulletins">
            <ExternalLink className="w-4 h-4 mr-1" /> Bulletins classe (PDF)
          </Button>
          <a href={`${API_BASE}/releves/pdf?class_id=${classId}&trimestre=${trimestre}`} target="_blank" rel="noreferrer">
            <Button variant="outline"><ExternalLink className="w-4 h-4 mr-1" /> Relevé de notes</Button>
          </a>
        </div>
      </div>
    </div>
  );
}
